(() => {
  if (window.DieuxAccessibeTriggerLoaded) return;
  window.DieuxAccessibeTriggerLoaded = true;

  const ACCESSIBE_SRC = 'https://acsbapp.com/apps/app/dist/js/app.js';
  const TRIGGER_ATTRIBUTE = 'data-acsb-custom-trigger';
  const TRIGGER_SELECTOR = `[${TRIGGER_ATTRIBUTE}="true"]`;
  const TRIGGER_HASH = '#accessibility';
  const ENABLED_STORAGE_KEY = 'dieux:accessibe-enabled';
  const ACCESSIBE_STATE_KEY = 'acsbState';

  let pendingTrigger = null;
  let loading = false;
  let openDispatched = false;

  /**
   * accessiBe's persisted widget state, or an empty object when absent or unreadable.
   *
   * @returns {{actions?: Object<string, {enabled?: boolean}>, widget?: {hidden?: boolean}}}
   */
  const readAccessibeState = () => {
    try {
      return JSON.parse(window.localStorage.getItem(ACCESSIBE_STATE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  };

  const readEnabled = () => {
    try {
      return window.localStorage.getItem(ENABLED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  };

  const writeEnabled = (enabled) => {
    try {
      if (enabled) window.localStorage.setItem(ENABLED_STORAGE_KEY, 'true');
      else window.localStorage.removeItem(ENABLED_STORAGE_KEY);
    } catch {}
  };

  const enableFromTrigger = () => {
    if (readAccessibeState().widget?.hidden) {
      try {
        window.localStorage.removeItem(ACCESSIBE_STATE_KEY);
      } catch {}
    }
    writeEnabled(true);
  };

  /**
   * Whether accessiBe should load on page load: enabled by a trigger or holding active adjustments, and not hidden.
   *
   * @returns {boolean}
   */
  const shouldRestore = () => {
    const state = readAccessibeState();
    if (state.widget?.hidden) {
      writeEnabled(false);
      return false;
    }
    const hasEnabledAction = Object.values(state.actions || {}).some((action) => action?.enabled);
    return readEnabled() || hasEnabledAction;
  };

  /**
   * The accessiBe trigger a click landed on, marking a `#accessibility` link as one on first use.
   *
   * @param {Element} target - Element the click landed on.
   * @returns {Element|null} Trigger element, or `null` when the click is not an accessiBe trigger.
   */
  const resolveTrigger = (target) => {
    const marked = target.closest(TRIGGER_SELECTOR);
    if (marked) return marked;

    const link = target.closest('a[href*="#"]');
    if (!link || link.hash.toLowerCase() !== TRIGGER_HASH) return null;

    link.setAttribute(TRIGGER_ATTRIBUTE, 'true');
    return link;
  };

  const accessibeListenerReady = () =>
    !!document.querySelector('.acsb-trigger')
    || (!!pendingTrigger && pendingTrigger.getAttribute('role') === 'button');

  const openWhenReady = () => {
    if (openDispatched) return;
    let attempts = 0;
    const attempt = () => {
      if (openDispatched) return;
      if (accessibeListenerReady()) {
        if (!pendingTrigger) {
          loading = false;
          return;
        }
        if (document.contains(pendingTrigger)) {
          openDispatched = true;
          loading = false;
          const trigger = pendingTrigger;
          pendingTrigger = null;
          trigger.click();
          return;
        }
      }
      if (++attempts > 150) {
        loading = false;
        return;
      }
      window.setTimeout(attempt, 60);
    };
    attempt();
  };

  const loadAccessibe = () => {
    const existing = document.querySelector('script[data-acsb-loader="true"]');
    if (existing) {
      openWhenReady();
      return;
    }
    const script = document.createElement('script');
    script.src = ACCESSIBE_SRC;
    script.async = true;
    script.dataset.acsbLoader = 'true';
    script.addEventListener(
      'load',
      () => {
        if (
          window.acsbJS
          && typeof window.acsbJS.init === 'function'
          && !window.DieuxAccessibeInitialized
          && !window.acsbJS.initialized
        ) {
          window.DieuxAccessibeInitialized = true;
          window.acsbJS.init();
        }
        openWhenReady();
      },
      { once: true }
    );
    document.head.appendChild(script);
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const trigger = resolveTrigger(event.target);
      if (!trigger) return;
      if (trigger.localName === 'a') event.preventDefault();
      enableFromTrigger();
      if ((window.acsbJS || window.accessWidget) && !loading) return;
      event.preventDefault();
      pendingTrigger = trigger;
      if (loading) return;
      loading = true;
      loadAccessibe();
    },
    true
  );

  if (shouldRestore() && !loading) {
    loading = true;
    loadAccessibe();
  }
})();
